import React from 'react';
import { Smartphone, Share, Plus } from 'lucide-react';
import { getDeferredPrompt, isIOS, isMobileDevice } from '../../lib/pwaInstall';

export type InstallPlatform =
    | 'android-with-prompt'
    | 'ios'
    | 'desktop'
    | 'unknown';

/**
 * Detect which install path applies to the current visitor. Kept as a
 * platform detector for callers that want to branch UI by device — even
 * though the install flow itself now only gates iOS (push works in
 * Android Chrome browser tabs without an install, and desktop callers
 * route through the install-instructions email instead of an inline
 * guide).
 */
export function detectInstallPlatform(): InstallPlatform {
    if (!isMobileDevice()) return 'desktop';
    if (isIOS()) return 'ios';
    if (getDeferredPrompt()) return 'android-with-prompt';
    return 'unknown';
}

interface NumberedStepProps {
    n: number;
    icon?: React.ReactNode;
    children: React.ReactNode;
}

const NumberedStep: React.FC<NumberedStepProps> = ({ n, icon, children }) => (
    <li className="flex items-start gap-3">
        <span className="w-6 h-6 rounded-full bg-rose-500 text-white text-xs font-medium flex items-center justify-center shrink-0 mt-0.5">
            {n}
        </span>
        <p className="text-sm text-stone-700 leading-relaxed flex-1">
            {children}
            {icon && (
                <span className="inline-flex items-center justify-center align-middle ml-1.5 text-stone-400">
                    {icon}
                </span>
            )}
        </p>
    </li>
);

interface PlatformInstallGuideProps {
    /**
     * Platform the caller has detected. Only `ios` renders an inline
     * guide now — Android (with or without a deferred prompt) gets the
     * native permission prompt directly, and desktop users receive the
     * install-instructions email. For any non-iOS platform this
     * component returns null so callers can safely include it without
     * conditional rendering at the call site.
     */
    platform: InstallPlatform;
}

const PlatformInstallGuide: React.FC<PlatformInstallGuideProps> = ({
    platform,
}) => {
    if (platform !== 'ios') return null;
    return (
        <ol className="space-y-3">
            <NumberedStep n={1} icon={<Share size={16} />}>
                Pulsa el icono Compartir en la barra inferior de Safari.
            </NumberedStep>
            <NumberedStep n={2} icon={<Plus size={16} />}>
                Selecciona <span className="font-medium">A&ntilde;adir a pantalla de inicio</span>.
            </NumberedStep>
            <NumberedStep n={3} icon={<Smartphone size={16} />}>
                Abre Ethernal desde el icono nuevo y permite las
                notificaciones cuando te lo pida. Es un paso obligatorio de
                Apple, no nuestro.
            </NumberedStep>
        </ol>
    );
};

export default PlatformInstallGuide;
